import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep, get, set } from 'lodash';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import type { MaasGroupSpec, MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init';
import { MaasRunMode as RunMode } from '@/delivery-type-plugin/capabilities/maas-init';
import {
  SYSTEM_VOLUME_NAME,
  VOLUME_SOURCE_EPHEMERAL,
  VOLUME_SOURCE_PVC_TEMPLATE,
} from '@/delivery-type-plugin/capabilities/maas-model-storage';
import { getWorkerGroupListForSubmit, stripStorageFromGroupSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups';
import { parseImageContainerDeployConfig } from '@/delivery-type-plugin/capabilities/maas-system-config';
import {
  getDataDiskMode,
  getVolumeClaimName,
  getVolumeMountPath,
  getVolumePvcTemplate,
  isCreateDataVolume,
  isCreateDataVolumeConfigured,
  isExistingDataVolume,
  normalizeDataVolumeForForm,
  normalizeDataVolumeForSubmit,
  normalizeSystemVolumeForSubmit,
  parseFormJsonField,
} from '@/delivery-type-plugin/plugins';

import { type ApplicationInstanceFormValues, DEFAULT_APP_DATA_MOUNT_PATH } from '../types';

const DEFAULT_SYSTEM_VOLUME = {
  name: SYSTEM_VOLUME_NAME,
  type: 'System',
  reclaimPolicy: 'Delete',
  size: '10Gi',
  source: {
    type: VOLUME_SOURCE_EPHEMERAL,
    mountPath: '/',
  },
};

export type AppImageVolumeKind = 'persistent' | 'ephemeral';

/** 应用实例存储组（挂载路径 + 可选数据盘） */
export interface AppStorageGroup {
  /** 表单内稳定标识，避免增删项时 React 复用错乱 */
  id?: string;
  mountPath: string;
  volume?: Record<string, unknown>;
  /** 由镜像 containerDeployConfig 回填，不可删除 */
  fromImage?: boolean;
  /** 镜像回填卷类型 */
  imageVolumeKind?: AppImageVolumeKind;
}

let appStorageGroupIdCounter = 0;

/** 旧版镜像回填存储卷 name 前缀（兼容历史数据） */
export const FORM_SYSTEM_VOLUME_NAME_PREFIX = 'formsystem_';

export function createAppStorageGroupId(): string {
  appStorageGroupIdCounter += 1;
  return `asg-${Date.now()}-${appStorageGroupIdCounter}`;
}

export function isFormSystemVolumeName(name?: unknown): boolean {
  return String(name ?? '')
    .trim()
    .startsWith(FORM_SYSTEM_VOLUME_NAME_PREFIX);
}

export const APP_STORAGE_GROUPS_FIELD = '_appStorageGroups';
export const MAX_APP_STORAGE_GROUPS = 20;

export interface ImageDeployVolumePaths {
  persistent: string[];
  ephemeral: string[];
}

/** 应用实例存储卷提交 name：data0、data1、data2 … */
export function resolveAppDataVolumeName(index: number): string {
  return `data${index}`;
}

export function resolveFormSystemVolumeNames(groups: AppStorageGroup[]): string[] {
  return groups
    .map((group, index) => (isImageBackfillGroup(group) ? resolveAppDataVolumeName(index) : undefined))
    .filter(Boolean) as string[];
}

export function parseAppStorageGroupsField(raw: unknown): AppStorageGroup[] {
  if (raw == null || raw === '') return [];
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const mountPath = String(row.mountPath ?? '').trim();
      const volume = row.volume;
      const volumeObj = volume && typeof volume === 'object' && !Array.isArray(volume) ? (volume as Record<string, unknown>) : undefined;
      if (!mountPath && !volumeObj) return null;
      const imageVolumeKind = row.imageVolumeKind === 'persistent' || row.imageVolumeKind === 'ephemeral' ? row.imageVolumeKind : undefined;
      const id = String(row.id ?? '').trim() || createAppStorageGroupId();
      const group: AppStorageGroup = {
        id,
        mountPath: mountPath || DEFAULT_APP_DATA_MOUNT_PATH,
        ...(volumeObj ? { volume: cloneDeep(volumeObj) } : {}),
        ...(row.fromImage ? { fromImage: true } : {}),
        ...(imageVolumeKind ? { imageVolumeKind } : {}),
      };
      if (!row.fromImage && isImageBackfillGroup(group)) {
        group.fromImage = true;
      }
      return group;
    })
    .filter(Boolean) as AppStorageGroup[];
}

export function writeAppStorageGroupsField(form: FormInstance, groups: AppStorageGroup[]) {
  form.setFieldValue(APP_STORAGE_GROUPS_FIELD, JSON.stringify(groups));
}

export function resolveImageDeployVolumePaths(imageItem?: CommonImageItemType | null): ImageDeployVolumePaths {
  const { persistentVolumeMountPath, persistentVolumeMountPaths, temporaryVolumeMountPath, temporaryVolumeMountPaths } =
    parseImageContainerDeployConfig(imageItem ?? undefined);

  const persistent = persistentVolumeMountPaths?.length
    ? persistentVolumeMountPaths.map((item) => String(item ?? '').trim()).filter(Boolean)
    : String(persistentVolumeMountPath ?? '').trim()
      ? [String(persistentVolumeMountPath).trim()]
      : [];

  const ephemeral = temporaryVolumeMountPaths?.length
    ? temporaryVolumeMountPaths.map((item) => String(item ?? '').trim()).filter(Boolean)
    : String(temporaryVolumeMountPath ?? '').trim()
      ? [String(temporaryVolumeMountPath).trim()]
      : [];

  return { persistent, ephemeral };
}

/** @deprecated 使用 resolveImageDeployVolumePaths */
export function resolveImageDeployMountPaths(imageItem?: CommonImageItemType | null): string[] {
  return resolveImageDeployVolumePaths(imageItem).persistent;
}

export function buildImagePersistentVolume(mountPath: string): Record<string, unknown> {
  return {
    type: 'Data',
    reclaimPolicy: 'Delete',
    source: {
      type: VOLUME_SOURCE_PVC_TEMPLATE,
      mountPath,
    },
  };
}

export function buildImageEphemeralVolume(mountPath: string): Record<string, unknown> {
  return {
    type: 'Data',
    reclaimPolicy: 'Delete',
    source: {
      type: VOLUME_SOURCE_EPHEMERAL,
      mountPath,
    },
  };
}

export function isImagePersistentPresetVolume(volume?: Record<string, unknown>): boolean {
  return get(volume, 'source.type') === VOLUME_SOURCE_PVC_TEMPLATE && !getVolumePvcTemplate(volume);
}

export function isImageEphemeralPresetVolume(volume?: Record<string, unknown>): boolean {
  return volume?.type === 'Data' && get(volume, 'source.type') === VOLUME_SOURCE_EPHEMERAL;
}

function isLegacyImagePresetVolume(volume?: Record<string, unknown>): boolean {
  if (!isFormSystemVolumeName(volume?.name)) return false;
  return isImagePersistentPresetVolume(volume) || isImageEphemeralPresetVolume(volume);
}

/** 判断详情中的 Data 卷是否为镜像预设（优先 annotations.formsystem，兼容旧 formsystem_ name 与卷结构） */
export function isImagePresetVolume(volume?: Record<string, unknown>, formsystemNames?: Set<string>): boolean {
  const name = String(volume?.name ?? '').trim();
  if (formsystemNames?.size) {
    return formsystemNames.has(name);
  }
  if (isLegacyImagePresetVolume(volume)) return true;
  return isImagePersistentPresetVolume(volume) || isImageEphemeralPresetVolume(volume);
}

/** 镜像回填存储组：显式 fromImage，或表单 JSON 中遗留的旧版 formsystem_ 卷 */
export function isImageBackfillGroup(group: AppStorageGroup): boolean {
  if (group.fromImage) return true;
  if (!group.volume) return false;
  return isLegacyImagePresetVolume(group.volume);
}

/** 用户自定义存储组（可增删改） */
export function isUserCustomStorageGroup(group: AppStorageGroup): boolean {
  return !isImageBackfillGroup(group);
}

/** 镜像回填的临时卷（不可配置数据盘） */
export function isEphemeralStorageGroup(group: AppStorageGroup): boolean {
  return isImageBackfillGroup(group) && group.imageVolumeKind === 'ephemeral';
}

/** 需要配置数据盘的持久卷（镜像持久卷或用户自定义组） */
export function isPersistentStorageGroup(group: AppStorageGroup): boolean {
  if (isEphemeralStorageGroup(group)) return false;
  if (isImageBackfillGroup(group)) return group.imageVolumeKind === 'persistent';
  return true;
}

/** 是否已配置有效数据盘（排除镜像预设占位卷） */
export function groupHasConfiguredDataDisk(group: AppStorageGroup): boolean {
  const volume = group.volume;
  if (!volume) return false;
  if (isImagePersistentPresetVolume(volume) || isImageEphemeralPresetVolume(volume)) return false;
  if (isExistingDataVolume(volume)) return !!getVolumeClaimName(volume);
  if (isCreateDataVolume(volume)) {
    return isCreateDataVolumeConfigured(volume);
  }
  return false;
}

/** 持久卷且已填写挂载路径时需配置数据盘 */
export function requiresDataDiskConfiguration(group: AppStorageGroup): boolean {
  if (!isPersistentStorageGroup(group)) return false;
  return !!String(group.mountPath || '').trim();
}

export function filterUserCustomStorageGroups(groups: AppStorageGroup[]): AppStorageGroup[] {
  return groups.filter(isUserCustomStorageGroup);
}

export function rebuildImageStorageGroups(imageItem?: CommonImageItemType | null): AppStorageGroup[] {
  const { persistent, ephemeral } = resolveImageDeployVolumePaths(imageItem);

  return [
    ...ephemeral.map(
      (mountPath): AppStorageGroup => ({
        id: createAppStorageGroupId(),
        mountPath,
        fromImage: true,
        imageVolumeKind: 'ephemeral',
        volume: buildImageEphemeralVolume(mountPath),
      }),
    ),
    ...persistent.map(
      (mountPath): AppStorageGroup => ({
        id: createAppStorageGroupId(),
        mountPath,
        fromImage: true,
        imageVolumeKind: 'persistent',
        volume: buildImagePersistentVolume(mountPath),
      }),
    ),
  ];
}

export function createUserCustomStorageGroup(mountPath = DEFAULT_APP_DATA_MOUNT_PATH): AppStorageGroup {
  return {
    id: createAppStorageGroupId(),
    mountPath,
  };
}

/** 编辑/详情：按镜像挂载路径标记镜像回填存储组 */
export function markImageStorageGroupsFromDetail(groups: AppStorageGroup[], imageItem?: unknown): AppStorageGroup[] {
  const parsedImage = parseFormJsonField<CommonImageItemType>(imageItem);
  const { persistent, ephemeral } = resolveImageDeployVolumePaths(parsedImage);
  if (!persistent.length && !ephemeral.length) return groups;

  const persistentSet = new Set(persistent);
  const ephemeralSet = new Set(ephemeral);

  return groups.map((group) => {
    const mountPath = String(group.mountPath || '').trim();
    if (persistentSet.has(mountPath) && isImagePersistentPresetVolume(group.volume)) {
      return { ...group, mountPath, fromImage: true, imageVolumeKind: 'persistent' as const };
    }
    if (ephemeralSet.has(mountPath) && isImageEphemeralPresetVolume(group.volume)) {
      return { ...group, mountPath, fromImage: true, imageVolumeKind: 'ephemeral' as const };
    }
    return group;
  });
}

/** @deprecated 使用 markImageStorageGroupsFromDetail */
export function markImageLockedAppStorageGroups(groups: AppStorageGroup[], imageItem?: unknown): AppStorageGroup[] {
  return markImageStorageGroupsFromDetail(groups, imageItem);
}

export function syncVolumeMountPath(volume: Record<string, unknown>, mountPath: string): Record<string, unknown> {
  const next = cloneDeep(volume);
  set(next, 'source.mountPath', mountPath);
  return next;
}

function normalizeImagePresetVolumeForSubmit(group: AppStorageGroup, volumeName: string): Record<string, unknown> {
  const mountPath = String(group.mountPath || '').trim() || DEFAULT_APP_DATA_MOUNT_PATH;
  const volume =
    group.imageVolumeKind === 'ephemeral' || isImageEphemeralPresetVolume(group.volume)
      ? buildImageEphemeralVolume(mountPath)
      : buildImagePersistentVolume(mountPath);
  volume.name = volumeName;
  return volume;
}

/** 将首组挂载路径同步到 dataMountPath（兼容仍读取该字段的逻辑） */
export function syncAppStorageMountPathField(form: FormInstance, groups: AppStorageGroup[]) {
  const first = groups[0];
  if (!first) {
    form.setFieldValue('dataMountPath', undefined);
    return;
  }
  form.setFieldValue('dataMountPath', first.mountPath || DEFAULT_APP_DATA_MOUNT_PATH);
}

export function readAppStorageGroupsFromForm(form: FormInstance): AppStorageGroup[] {
  return parseAppStorageGroupsField(form.getFieldValue(APP_STORAGE_GROUPS_FIELD));
}

/** 选中镜像后按 containerDeployConfig 回填存储组（在前），保留用户自定义组（在后） */
export function applyImageVolumesToAppStorageGroups(
  form: FormInstance,
  imageItem?: CommonImageItemType | null,
  preservedUserGroups?: AppStorageGroup[],
) {
  const userGroups = preservedUserGroups ?? filterUserCustomStorageGroups(readAppStorageGroupsFromForm(form));
  const imageGroups = rebuildImageStorageGroups(imageItem);
  const next = [...imageGroups, ...userGroups];

  writeAppStorageGroupsField(form, next);
  syncAppStorageMountPathField(form, next);
}

/** 编辑/详情：镜像回填项在前，用户自定义项在后 */
export function buildAppStorageGroupsFromDetailVolumes(
  dataVolumes: Record<string, unknown>[],
  imageItem?: unknown,
  formsystemNames?: string[],
): AppStorageGroup[] {
  if (!dataVolumes.length) return [];

  const parsedImage = parseFormJsonField<CommonImageItemType>(imageItem);
  const imageTemplateGroups = rebuildImageStorageGroups(parsedImage);
  const formsystemSet = new Set((formsystemNames ?? []).map((name) => String(name).trim()).filter(Boolean));
  const imageVolumes = dataVolumes.filter((volume) => isImagePresetVolume(volume, formsystemSet));
  const customVolumes = dataVolumes.filter((volume) => !isImagePresetVolume(volume, formsystemSet));

  const imageGroups: AppStorageGroup[] = imageTemplateGroups.length
    ? imageTemplateGroups.map((template) => {
        const matched = imageVolumes.find((volume) => {
          const mountPath = getVolumeMountPath(volume);
          const kind = isImageEphemeralPresetVolume(volume) ? 'ephemeral' : 'persistent';
          return mountPath === template.mountPath && kind === template.imageVolumeKind;
        });
        return matched
          ? {
              ...template,
              volume: syncVolumeMountPath(matched, template.mountPath),
            }
          : template;
      })
    : imageVolumes.map((volume) => {
        const mountPath = getVolumeMountPath(volume) || DEFAULT_APP_DATA_MOUNT_PATH;
        const normalizedMountPath = String(mountPath).trim() || DEFAULT_APP_DATA_MOUNT_PATH;
        return {
          id: createAppStorageGroupId(),
          mountPath: normalizedMountPath,
          fromImage: true,
          imageVolumeKind: isImageEphemeralPresetVolume(volume) ? ('ephemeral' as const) : ('persistent' as const),
          volume: syncVolumeMountPath(volume, normalizedMountPath),
        };
      });

  const customGroups = customVolumes.map((volume) => {
    const mountPath = getVolumeMountPath(volume) || DEFAULT_APP_DATA_MOUNT_PATH;
    const normalizedMountPath = String(mountPath).trim() || DEFAULT_APP_DATA_MOUNT_PATH;
    return {
      id: createAppStorageGroupId(),
      mountPath: normalizedMountPath,
      volume: normalizeDataVolumeForForm(volume, normalizedMountPath),
    };
  });

  return [...imageGroups, ...customGroups];
}

export function parseAppStorageGroupsFromValues(values: ApplicationInstanceFormValues): AppStorageGroup[] {
  return parseAppStorageGroupsField(values[APP_STORAGE_GROUPS_FIELD as keyof ApplicationInstanceFormValues]);
}

/** 从存储组构建待提交的 Data 卷列表 */
export function buildAppDataVolumesForSubmit(groups: AppStorageGroup[]): Record<string, unknown>[] {
  return groups
    .map((group, index) => {
      const volumeName = resolveAppDataVolumeName(index);
      if (isEphemeralStorageGroup(group)) {
        return normalizeImagePresetVolumeForSubmit(group, volumeName);
      }
      if (!groupHasConfiguredDataDisk(group)) return undefined;
      return normalizeDataVolumeForSubmit(group.volume!, group.mountPath, {
        volumeName,
      });
    })
    .filter(Boolean) as Record<string, unknown>[];
}

/** 提交前将多组 Data 卷组装回节点组 spec（volume 数组结构） */
export function attachAppStorageVolumesForSubmit<T extends MaasSharedFormValues>(values: T): T {
  const next = cloneDeep(values);
  const dataVolumes = buildAppDataVolumesForSubmit(parseAppStorageGroupsFromValues(next as ApplicationInstanceFormValues));

  const attachToGroup = (
    group: MaasGroupSpec | undefined,
    groupKey: 'headGroupSpec' | 'workerGroupSpecs[0]',
  ): MaasGroupSpec | undefined => {
    if (!group) return group;
    const stripped = stripStorageFromGroupSpec(group)!;
    const systemDiskGi = get(next, `${groupKey === 'headGroupSpec' ? 'master' : 'worker'}.systemDiskSize`);
    const volumes: Record<string, unknown>[] = [];
    if (typeof systemDiskGi === 'number' && systemDiskGi > 0) {
      volumes.push(normalizeSystemVolumeForSubmit(cloneDeep(DEFAULT_SYSTEM_VOLUME), systemDiskGi));
    }
    dataVolumes.forEach((volume) => volumes.push(cloneDeep(volume)));
    set(stripped, 'resource.storage.volume', volumes);
    return stripped;
  };

  const runMode = next.runMode;

  if (runMode === RunMode.PrimarySecondary && next.headGroupSpec) {
    next.headGroupSpec = attachToGroup(next.headGroupSpec, 'headGroupSpec');
  }

  const workers = getWorkerGroupListForSubmit(next);
  if (workers.length > 0) {
    next.workerGroupSpecs = workers.map((worker) => attachToGroup(worker, 'workerGroupSpecs[0]')!);
  }

  return next;
}

export function findAllDataVolumesInGroup(volumes?: Record<string, unknown>[]): Record<string, unknown>[] {
  return (volumes || []).filter((v) => v.type === 'Data' || String(v.type || '').toLowerCase() === 'data');
}

export function resolveAppDataMountPaths(values: ApplicationInstanceFormValues): string[] {
  const groups = parseAppStorageGroupsFromValues(values);
  const paths = groups.map((g) => String(g.mountPath || '').trim()).filter(Boolean);
  return paths.length ? paths : [DEFAULT_APP_DATA_MOUNT_PATH];
}

export function resolveAppDataMountPath(values: ApplicationInstanceFormValues): string {
  return resolveAppDataMountPaths(values)[0] || DEFAULT_APP_DATA_MOUNT_PATH;
}
